import type { AppState, FoodDefinition, LoggedFoodEntry, Macros } from "./types";

export const ZERO_MACROS: Macros = { calories: 0, protein: 0, carbs: 0, fat: 0 };

export function roundMacro(value: number): number {
  return Math.round((Number(value) + Number.EPSILON) * 10) / 10;
}

export function addMacros(...items: Array<Partial<Macros> | undefined>): Macros {
  return items.reduce<Macros>(
    (sum, item) => ({
      calories: roundMacro(sum.calories + Number(item?.calories ?? 0)),
      protein: roundMacro(sum.protein + Number(item?.protein ?? 0)),
      carbs: roundMacro(sum.carbs + Number(item?.carbs ?? 0)),
      fat: roundMacro(sum.fat + Number(item?.fat ?? 0))
    }),
    { ...ZERO_MACROS }
  );
}

export function scaleMacros(per100g: Macros, grams: number): Macros {
  if (!Number.isFinite(grams) || grams <= 0) throw new Error("Food amount must be greater than zero.");
  const factor = grams / 100;
  return {
    calories: roundMacro(per100g.calories * factor),
    protein: roundMacro(per100g.protein * factor),
    carbs: roundMacro(per100g.carbs * factor),
    fat: roundMacro(per100g.fat * factor)
  };
}

export function normalizeSearchText(value: unknown): string {
  return String(value ?? "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9\u0600-\u06ff]+/g, " ")
    .trim();
}

export function searchTokens(value: unknown): string[] {
  return [...new Set(normalizeSearchText(value).split(/\s+/).filter(Boolean))].slice(0, 24);
}

export function foodMatchesSearch(food: Pick<FoodDefinition, "tokens">, query: string): boolean {
  const wanted = searchTokens(query);
  return wanted.length > 0 && wanted.every((token) => food.tokens.some((candidate) => candidate.startsWith(token)));
}

export function validateFoodDefinition(value: unknown): value is FoodDefinition {
  if (!value || typeof value !== "object") return false;
  const food = value as FoodDefinition;
  if (!food.id || !food.providerId || !food.name || !food.catalogVersion) return false;
  if (!food.per100g || !Array.isArray(food.portions) || !Array.isArray(food.tokens)) return false;
  const macroValues = [food.per100g.calories, food.per100g.protein, food.per100g.carbs, food.per100g.fat];
  if (macroValues.some((v) => !Number.isFinite(v) || v < 0)) return false;
  if (food.per100g.calories > 1000 || food.per100g.protein > 100 || food.per100g.carbs > 100 || food.per100g.fat > 100) return false;
  return food.portions.every((portion) => portion.label && Number.isFinite(portion.grams) && portion.grams > 0);
}

export function createLoggedFoodEntry(
  food: FoodDefinition,
  date: string,
  mealId: string,
  grams: number,
  existingId?: string
): LoggedFoodEntry {
  if (!validateFoodDefinition(food)) throw new Error("This food record is incomplete or invalid.");
  const now = new Date().toISOString();
  return {
    id: existingId ?? crypto.randomUUID(),
    date,
    mealId,
    foodId: food.id,
    provider: food.provider,
    providerId: food.providerId,
    sourceVersion: food.catalogVersion,
    name: food.name,
    description: food.description,
    quantity: roundMacro(grams),
    unit: "g",
    grams: roundMacro(grams),
    macros: scaleMacros(food.per100g, grams),
    createdAt: now,
    updatedAt: now
  };
}

export function getOffPlanEntries(state: Pick<AppState, "offPlan">, date: string, mealId?: string): LoggedFoodEntry[] {
  const day = state.offPlan?.[date] ?? {};
  return mealId ? [...(day[mealId] ?? [])] : Object.values(day).flat();
}

export function offPlanTotals(entries: LoggedFoodEntry[]): Macros {
  return addMacros(...entries.map((entry) => entry.macros));
}

export function neutralizeSpreadsheetFormula(value: unknown): string {
  const text = String(value ?? "");
  return /^[=+\-@]/.test(text.trimStart()) ? `'${text}` : text;
}

export function csvCell(value: unknown): string {
  return `"${neutralizeSpreadsheetFormula(value).replace(/"/g, '""')}"`;
}

export function normalizeAppState(input: unknown): AppState {
  const source = input && typeof input === "object" ? (input as Record<string, unknown>) : {};
  const state: AppState = {
    ...source,
    log: isRecord(source.log) ? (source.log as AppState["log"]) : {},
    w: normalizeMeasures(source.w),
    cs: normalizeMeasures(source.cs),
    st: normalizeMeasures(source.st),
    rewarded: isRecord(source.rewarded) ? (source.rewarded as Record<string, boolean>) : {},
    rankRewards: isRecord(source.rankRewards) ? (source.rankRewards as Record<string, boolean>) : {},
    rankRewardStartLevel: Number(source.rankRewardStartLevel) || 1,
    notes: isRecord(source.notes) ? (source.notes as AppState["notes"]) : {},
    offPlan: isRecord(source.offPlan) ? (source.offPlan as AppState["offPlan"]) : {},
    customFoods: Array.isArray(source.customFoods) ? source.customFoods.filter(validateFoodDefinition) : [],
    schemaVersion: 2,
    updatedAt: typeof source.updatedAt === "string" ? source.updatedAt : undefined
  };
  return state;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function normalizeMeasures(value: unknown): Array<{ d: string; v: number }> {
  if (!Array.isArray(value)) return [];
  return value
    .filter((entry) => entry && typeof entry.d === "string" && Number.isFinite(Number(entry.v)))
    .map((entry) => ({ d: entry.d, v: Number(entry.v) }))
    .sort((a, b) => a.d.localeCompare(b.d));
}
